import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { formatarTelefone } from '@/pages/painel/lib/telefone';

interface ItemPendente {
  id: number;
  nome: string | null;
  telefone: string | null;
  status: string;
  match_motivo: string | null;
  candidatos: { id: number; nome: string }[] | null;
  import_batch_id: number | null;
}

interface Empresa {
  id: number;
  nome: string;
  cidade: string | null;
  uf: string | null;
}

export default function CorrespondenciaPendente({ password }: { password: string }) {
  const [itens, setItens] = useState<ItemPendente[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [filtro, setFiltro] = useState<'todos' | 'aguardando_empresa' | 'duplicidade'>('todos');
  const [selecionados, setSelecionados] = useState<number[]>([]);
  const [empresaBulk, setEmpresaBulk] = useState('');
  const [empresaPorItem, setEmpresaPorItem] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [promovendo, setPromovendo] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [pend, emp] = await Promise.all([
        supabase.functions.invoke('whatsapp-import', { body: { action: 'pendentes', password } }),
        supabase.functions.invoke('crm-core', { body: { action: 'list_empresas', password } }),
      ]);
      const p = pend.data as { itens?: ItemPendente[]; error?: string };
      if (p?.error) throw new Error(p.error);
      setItens(p?.itens ?? []);
      const e = emp.data as { empresas?: Empresa[] };
      setEmpresas(e?.empresas ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar pendências.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const visiveis = itens.filter((i) => filtro === 'todos' || i.status === filtro);

  const toggle = (id: number) => {
    setSelecionados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const promover = async (ids: number[], empresaId: string) => {
    if (!ids.length || !empresaId) return;
    setPromovendo(true);
    setError(null);
    setMsg(null);
    try {
      const { data, error: e } = await supabase.functions.invoke('whatsapp-import', {
        body: { action: 'promover', password, staging_ids: ids, empresa_id: Number(empresaId) },
      });
      if (e) throw new Error(await extractErrorMessage(e));
      const parsed = data as { ok?: boolean; promovidos?: number; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setMsg(`${parsed?.promovidos ?? 0} contato(s) importado(s) e vinculado(s).`);
      setSelecionados([]);
      setEmpresaBulk('');
      setEmpresaPorItem({});
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao promover.');
    } finally {
      setPromovendo(false);
    }
  };

  const SelectEmpresa = ({ value, onChange, compact }: { value: string; onChange: (v: string) => void; compact?: boolean }) => (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`border border-gray-200 rounded-lg bg-white cursor-pointer text-gray-700 focus:outline-none focus:border-[#E8610A] ${compact ? 'px-2 py-1.5 text-xs max-w-[240px]' : 'px-3 py-2 text-sm'}`}
    >
      <option value="">— vincular a empresa —</option>
      {empresas.map((emp) => (
        <option key={emp.id} value={emp.id}>{emp.nome}{emp.cidade ? ` — ${emp.cidade}${emp.uf ? `/${emp.uf}` : ''}` : ''}</option>
      ))}
    </select>
  );

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <i className="ri-links-line text-[#E8610A]"></i>
          <h3 className="text-sm font-bold text-gray-900">Fila de correspondência pendente</h3>
        </div>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap disabled:opacity-60">
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i> Atualizar
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Contatos importados que ainda não têm empresa identificável, ou com mais de uma correspondência possível. Nada é fundido automaticamente — você decide o vínculo.
      </p>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-3">{error}</div>}
      {msg && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-3">{msg}</div>}

      <div className="flex items-center gap-1.5 flex-wrap mb-4">
        {([['todos', 'Todos'], ['aguardando_empresa', 'Sem empresa'], ['duplicidade', 'Possível duplicidade']] as const).map(([k, label]) => (
          <button key={k} onClick={() => setFiltro(k)} className={`text-xs font-semibold rounded-full px-3 py-1.5 border cursor-pointer whitespace-nowrap transition-colors ${filtro === k ? 'bg-[#E8610A] text-white border-[#E8610A]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#E8610A]'}`}>
            {label}
          </button>
        ))}
      </div>

      {selecionados.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap bg-[#E8610A]/5 border border-[#E8610A]/20 rounded-xl px-3 py-2 mb-3">
          <span className="text-xs font-semibold text-gray-700">{selecionados.length} selecionado(s)</span>
          <SelectEmpresa value={empresaBulk} onChange={setEmpresaBulk} compact />
          <button
            onClick={() => promover(selecionados, empresaBulk)}
            disabled={!empresaBulk || promovendo}
            className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg px-3 py-1.5 cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {promovendo ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-link"></i>} Importar selecionados
          </button>
        </div>
      )}

      {loading && visiveis.length === 0 ? (
        <div className="flex justify-center py-10"><div className="w-7 h-7 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" /></div>
      ) : visiveis.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 flex items-center justify-center bg-green-50 text-green-600 rounded-full mx-auto mb-3">
            <i className="ri-check-double-line text-xl"></i>
          </div>
          <p className="text-sm text-gray-500">Nenhuma correspondência pendente.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visiveis.map((it) => (
            <div key={it.id} className="border border-gray-100 rounded-xl p-3">
              <div className="flex items-start gap-3">
                <input type="checkbox" checked={selecionados.includes(it.id)} onChange={() => toggle(it.id)} className="mt-1 cursor-pointer" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-gray-900">{it.nome || 'Sem nome'}</p>
                    <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${it.status === 'duplicidade' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                      {it.status === 'duplicidade' ? 'Possível duplicidade' : 'Sem empresa'}
                    </span>
                    {it.telefone && <span className="text-xs text-gray-500">{formatarTelefone(it.telefone)}</span>}
                  </div>
                  {it.match_motivo && <p className="text-xs text-gray-400 mt-0.5">{it.match_motivo}</p>}
                  {it.candidatos && it.candidatos.length > 0 && (
                    <p className="text-[11px] text-gray-400 mt-1">
                      Correspondências no CRM: {it.candidatos.map((c) => c.nome).join(', ')}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <SelectEmpresa value={empresaPorItem[it.id] ?? ''} onChange={(v) => setEmpresaPorItem((p) => ({ ...p, [it.id]: v }))} compact />
                  <button
                    onClick={() => promover([it.id], empresaPorItem[it.id] ?? '')}
                    disabled={!empresaPorItem[it.id] || promovendo}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-gray-800 hover:bg-gray-700 rounded-lg px-3 py-1.5 cursor-pointer whitespace-nowrap disabled:opacity-40"
                  >
                    Importar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}